package org.zest.insan_360_website.utils

import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import org.w3c.fetch.Response
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.ApiResponse
import org.zest.insan_360_website.model.GenericResponse

val apiJsonParser = Json { ignoreUnknownKeys = true }

suspend inline fun <reified T> processResponse(
    response: Response,
    toast: ToastState? = null,
    fallbackErrorLog: String? = null,
    crossinline onSuccess: suspend () -> Unit = {}
): T? {
    val responseText = response.text().await()
    val displayedErrorLog = fallbackErrorLog ?: "Server rejected task"

    if (response.ok) {
        if (Unit is T) {
            val result =
                try { apiJsonParser.decodeFromString<GenericResponse>(responseText) }
                catch (e: Exception) {
                    console.error("Failed to parse GenericResponse: ${e.message}")
                    return null
                }
            provideGenericResponse(result, toast) { onSuccess() }
            return if (result.success) Unit as T else null
        } else {
            val result =
                try { apiJsonParser.decodeFromString<ApiResponse<T>>(responseText) }
                catch (e: Exception) {
                    console.error("Failed to parse ApiResponse: ${e.message}")
                    return null
                }
            return provideApiResponse(result, toast) { onSuccess() }
        }
    } else {
        try {
            val result = apiJsonParser.decodeFromString<GenericResponse>(responseText)
            val localizedErrorMessage = LanguageSettings.ApiErrors.getMessage(
                errorCode = result.errorCode,
                language = UserPreferences.language
            )

            toast?.show(localizedErrorMessage, ToastType.ERROR)
            console.error("$displayedErrorLog: ${result.debugMessage ?: result.errorCode?.name}")

        } catch (e: Exception) {
            val fallbackMsg = LanguageSettings.ApiErrors.getMessage(null, UserPreferences.language)
            toast?.show(fallbackMsg, ToastType.ERROR)
            console.error("$displayedErrorLog: HTTP Error ${response.status}. Raw Response: $responseText")
        }
        return null
    }
}

suspend fun provideGenericResponse(
    result: GenericResponse,
    toast: ToastState?,
    onSuccess: suspend () -> Unit = {}
) {
    if (result.success) {
        val successMsg = LanguageSettings.ApiSuccess.getMessage(result.successCode, UserPreferences.language)
        toast?.show(successMsg, ToastType.SUCCESS)
        onSuccess()
    } else {
        val errorMsg = LanguageSettings.ApiErrors.getMessage(result.errorCode, UserPreferences.language)
        toast?.show(errorMsg, ToastType.ERROR)
        console.error("API Action Failed: ${result.debugMessage}")
    }
}

suspend fun <T> provideApiResponse(
    result: ApiResponse<T>,
    toast: ToastState?,
    onSuccess: suspend () -> Unit = {}
): T? {
    if (result.success) {
        if (result.successCode != null) {
            val successMsg = LanguageSettings.ApiSuccess.getMessage(result.successCode, UserPreferences.language)
            toast?.show(successMsg, ToastType.SUCCESS)
        }
        onSuccess()
        return result.data
    } else {
        val fallbackMsg = LanguageSettings.ApiErrors.getMessage(null, UserPreferences.language)
        toast?.show(fallbackMsg, ToastType.ERROR)
        console.error("API Action Failed: ${result.debugMessage}")
        return null
    }
}